<template>
  <div>
    <pa-admin-toolbar
      :per-page="perPage"
      :search.sync="searchInput"
      :from.sync="fromInput"
      :to.sync="toInput"
      :busy="loading"
      @per-page="setPerPage"
      @search="applySearch"
    >
      <button type="button" class="paa-btn paa-btn--secondary" :disabled="loading || !total" @click="exportRows">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" /></svg>
        Export All
      </button>
    </pa-admin-toolbar>

    <div class="paa-filters">
      <pa-admin-filter label="SEO Manager" :value="filters.campaignManager" :options="managerOptions" @input="setFilter('campaignManager', $event)" />
      <pa-admin-filter label="Package" :value="filters.package" :options="packageOptions" @input="setFilter('package', $event)" />
    </div>

    <div v-if="loadError" class="paa-error">{{ loadError }}</div>

    <div class="paa-stat-row">
      <span class="paa-muted">{{ stats.invoicesCount || 0 }} monthly invoices in range</span>
      <span class="paa-muted">Active subscriptions: <b>{{ stats.activeSubscriptions || 0 }}</b></span>
      <span class="paa-muted">MRR: <b>{{ money(stats.mrr) || '$0.00' }}</b></span>
      <span class="paa-spacer"></span>
      <b>Total paid: {{ money(totalPaid) || '$0.00' }}</b>
    </div>

    <div class="paa-card paa-tblwrap">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th :class="thClass('userId')" @click="sortBy('userId')">{{ sortMark('userId') }}User ID</th>
            <th :class="thClass('userEmail')" @click="sortBy('userEmail')">{{ sortMark('userEmail') }}Email</th>
            <th :class="thClass('id', 'paa-r')" @click="sortBy('id')">{{ sortMark('id') }}Invoice ID</th>
            <th :class="thClass('invoiceDate')" @click="sortBy('invoiceDate')">{{ sortMark('invoiceDate') }}Invoice date</th>
            <th>Package</th>
            <th class="paa-r">Top Level</th>
            <th class="paa-r">Supporting</th>
            <th :class="thClass('month', 'paa-r')" @click="sortBy('month')">{{ sortMark('month') }}Month</th>
            <th>Billing</th>
            <th>SEO Manager</th>
            <th :class="thClass('amount', 'paa-r')" @click="sortBy('amount')">{{ sortMark('amount') }}Amount ($)</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in records" :key="row.key">
            <td>{{ row.userId }}</td>
            <td>
              <a :href="userAccountsHref(row.userEmail)" target="_blank" rel="noopener">{{ row.userEmail }}</a>
              <span class="paa-sub">{{ row.siteName }}</span>
            </td>
            <td class="paa-r">
              <template v-if="row.id">{{ row.id }}</template>
              <span v-else class="paa-muted" title="The invoice gets its ID when it is paid">&ndash;</span>
            </td>
            <td>{{ row.invoiceDate || '' }}</td>
            <td>{{ row.package ? row.package.name : '' }}</td>
            <td class="paa-r">{{ row.package ? row.package.tl : '' }}</td>
            <td class="paa-r">{{ row.package ? row.package.supporting : '' }}</td>
            <td class="paa-r">{{ row.month }}</td>
            <td>{{ row.billing }}</td>
            <td>{{ row.manager ? row.manager.name : '' }}</td>
            <td class="paa-r">{{ money(row.amount) }}</td>
            <td><pa-admin-badge :badge="row.status" /></td>
          </tr>
          <tr v-if="!records.length">
            <td class="paa-empty" colspan="12">{{ loading ? 'Loading…' : 'No package invoice matches this search.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pa-admin-pager :page="page" :page-count="pageCount" @change="setPage" />
  </div>
</template>

<script>
import PaAdminBadge from './PaAdminBadge.vue'
import PaAdminFilter from './PaAdminFilter.vue'
import PaAdminPager from './PaAdminPager.vue'
import PaAdminToolbar from './PaAdminToolbar.vue'
import { PA_ADMIN_PACKAGE_OPTIONS } from './paAdminConstants'
import paAdminListMixin from './paAdminListMixin'

/**
 * Package Invoices Timeline: the monthly invoices of the package subscriptions. A package has no
 * fixed term, so the month is a plain number. Status: Sent / Overdue (first invoice, emailed),
 * Paid, Card declined (a following month the card could not pay).
 */
export default {
  name: 'AdminPopAuthorityPackageInvoicesTimeline',
  components: { PaAdminBadge, PaAdminFilter, PaAdminPager, PaAdminToolbar },
  mixins: [paAdminListMixin],
  paAdmin: { action: 'getPopAuthorityPackageInvoicesForAdmin', sort: 'invoiceDate', tab: 'package-invoices' },
  data () {
    return {
      filters: { campaignManager: 'all', package: 'all' },
      packageOptions: PA_ADMIN_PACKAGE_OPTIONS,
    }
  },
  methods: {
    exportRows () {
      const header = [
        'User ID', 'Email', 'Project', 'Invoice ID', 'Invoice date', 'Paid on', 'Package', 'Top Level',
        'Supporting', 'Month', 'Billing', 'SEO Manager', 'Amount ($)', 'Status',
      ]
      return this.exportCsv(header, (row) => [
        row.userId, row.userEmail, row.siteName, row.id || '', row.invoiceDate || '', row.paidAt || '',
        row.package ? row.package.name : '', row.package ? row.package.tl : '',
        row.package ? row.package.supporting : '', row.month, row.billing,
        row.manager ? row.manager.name : '', row.amount, row.status ? row.status.label : '',
      ])
    },
  },
}
</script>
